import React from "react";
import { ExternalLink, GitPullRequest, Landmark } from "lucide-react";
import type { AssuranceAssessment } from "../../../assurance/types";
import {
  selectBenchmark,
  selectCompliance,
  selectDecision,
  selectPolicies,
} from "../../../assurance/model";
import {
  CaveatNote,
  CompareBar,
  EmptyPanel,
  FindingLink,
  MetricTile,
  Pill,
  SectionPanel,
  StatusPill,
  fmtNumber,
  statusTone,
} from "../primitives";

// ---------------------------------------------------------------------------
// Compliance mapping
// ---------------------------------------------------------------------------

export function ComplianceView({
  assessment,
  demo,
  onSelectFindingId,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
  onSelectFindingId: (findingId: string) => void;
}) {
  const mappings = selectCompliance(assessment);
  const findingById = new Map(
    assessment.findings.map((finding) => [finding.id, finding])
  );
  const satisfied = mappings.filter(
    (mapping) => statusTone(mapping.status) === "success"
  ).length;
  const gaps = mappings.filter(
    (mapping) => statusTone(mapping.status) === "critical"
  ).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <MetricTile
          label="Frameworks mapped"
          value={mappings.length ? fmtNumber(new Set(mappings.map((mapping) => mapping.framework)).size) : undefined}
          demo={demo}
        />
        <MetricTile
          label="Control mappings"
          value={mappings.length ? fmtNumber(mappings.length) : undefined}
          demo={demo}
        />
        <MetricTile
          label="Evidence-supported"
          value={mappings.length ? fmtNumber(satisfied) : undefined}
          tone="success"
          demo={demo}
        />
        <MetricTile
          label="Gaps"
          value={mappings.length ? fmtNumber(gaps) : undefined}
          tone={gaps ? "critical" : "success"}
          demo={demo}
        />
      </div>

      <SectionPanel
        title="Control mappings"
        description="Where current assessment evidence supports each framework control"
        flush
      >
        {mappings.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Framework</th>
                  <th scope="col">Controls</th>
                  <th scope="col">Status</th>
                  <th scope="col">Supporting evidence</th>
                  <th scope="col">Note</th>
                </tr>
              </thead>
              <tbody>
                {mappings.map((mapping, index) => (
                  <tr key={`${mapping.framework}-${index}`}>
                    <td className="whitespace-nowrap font-medium text-text">
                      <span className="inline-flex items-center gap-2">
                        <Landmark className="h-3.5 w-3.5 text-muted" aria-hidden="true" />
                        {mapping.framework}
                      </span>
                    </td>
                    <td className="whitespace-nowrap font-mono text-xs">{mapping.controls || "—"}</td>
                    <td className="whitespace-nowrap">
                      <StatusPill status={mapping.status} demo={demo} />
                    </td>
                    <td className="max-w-sm">
                      {mapping.evidence.length ? (
                        <ul className="space-y-1 text-[12.5px] text-text2">
                          {mapping.evidence.map((entry, entryIndex) => (
                            <li key={entryIndex} className="flex items-start gap-1.5">
                              <span className="mt-2 h-1 w-1 flex-none rounded-full bg-muted" aria-hidden="true" />
                              {/^PAX-/.test(entry) ? (
                                <FindingLink
                                  id={entry}
                                  finding={findingById.get(entry)}
                                  onOpen={(item) => onSelectFindingId(item.id)}
                                />
                              ) : (
                                entry
                              )}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="max-w-xs text-muted">{mapping.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel demo={demo} />
          </div>
        )}
      </SectionPanel>

      <CaveatNote tone="warning">
        Control mapping shows where measured evidence is relevant to a framework.
        It is not certification, an audit opinion, or a compliance attestation.
      </CaveatNote>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Benchmark
// ---------------------------------------------------------------------------

export function BenchmarkView({
  assessment,
  demo,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
}) {
  const benchmark = selectBenchmark(assessment);
  const belowBaseline = benchmark.metrics.filter((metric) => {
    if (metric.value === undefined || metric.cohortMedian === undefined) return false;
    return metric.direction === "lower-better"
      ? metric.value > metric.cohortMedian
      : metric.value < metric.cohortMedian;
  }).length;

  return (
    <div className="space-y-4">
      {benchmark.nature && <CaveatNote tone="warning">{benchmark.nature}</CaveatNote>}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        <MetricTile
          label="Overall percentile"
          value={
            benchmark.overallPercentile !== undefined
              ? `P${fmtNumber(benchmark.overallPercentile)}`
              : undefined
          }
          sub={benchmark.cohort}
          tone={
            benchmark.overallPercentile === undefined
              ? "neutral"
              : benchmark.overallPercentile >= 50
                ? "success"
                : "warning"
          }
          demo={demo}
        />
        <MetricTile
          label="Metrics compared"
          value={benchmark.metrics.length ? fmtNumber(benchmark.metrics.length) : undefined}
          demo={demo}
        />
        <MetricTile
          label="Behind cohort median"
          value={benchmark.metrics.length ? fmtNumber(belowBaseline) : undefined}
          tone={belowBaseline ? "warning" : "success"}
          demo={demo}
        />
      </div>

      <SectionPanel
        title="Metric comparison"
        description="This application vs. the cohort median — direction-aware"
        flush
      >
        {benchmark.metrics.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Metric</th>
                  <th scope="col" className="min-w-[220px]">You vs. cohort median</th>
                  <th scope="col" className="text-right">Percentile</th>
                  <th scope="col">Better is</th>
                </tr>
              </thead>
              <tbody>
                {benchmark.metrics.map((metric, index) => (
                  <tr key={metric.name || index}>
                    <td className="max-w-[240px] font-medium text-text">{metric.name}</td>
                    <td>
                      {metric.value !== undefined && metric.cohortMedian !== undefined ? (
                        <CompareBar
                          value={metric.value}
                          median={metric.cohortMedian}
                          direction={metric.direction}
                        />
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="whitespace-nowrap text-right">
                      {metric.percentile !== undefined ? (
                        <span
                          className={
                            metric.percentile >= 50
                              ? "font-semibold tabular-nums text-success"
                              : "font-semibold tabular-nums text-high"
                          }
                        >
                          P{metric.percentile}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="whitespace-nowrap text-muted">
                      {metric.direction === "lower-better" ? "Lower" : "Higher"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel demo={demo} />
          </div>
        )}
      </SectionPanel>

      {benchmark.references.length > 0 && (
        <SectionPanel
          title="Benchmark references"
          description="Public material used to shape structure — not validated equivalence claims"
        >
          <ul className="space-y-2">
            {benchmark.references.map((reference, index) => (
              <li key={index} className="text-xs leading-5">
                {reference.url ? (
                  <a
                    href={reference.url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
                  >
                    {reference.title || reference.url}
                    <ExternalLink className="h-3 w-3" aria-hidden="true" />
                  </a>
                ) : (
                  <span className="font-medium text-text">{reference.title}</span>
                )}
                {reference.use && <span className="text-muted"> — {reference.use}</span>}
              </li>
            ))}
          </ul>
        </SectionPanel>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PR guard
// ---------------------------------------------------------------------------

export function PrGuardView({
  assessment,
  demo,
}: {
  assessment: AssuranceAssessment;
  demo: boolean;
}) {
  const policies = selectPolicies(assessment);
  const decision = selectDecision(assessment);

  const banner: Record<string, { tone: "critical" | "warning" | "success" | "neutral"; label: string; detail: string }> = {
    blocked: {
      tone: "critical",
      label: "Merge blocked",
      detail:
        "One or more policy gates fail against current evidence. Merging requires fixing the findings or an approved, time-boxed exception.",
    },
    review: {
      tone: "warning",
      label: "Review required",
      detail: "No hard failures, but some gates need a human decision before merge.",
    },
    pass: {
      tone: "success",
      label: "Gates passing",
      detail: "All evaluated policy gates are satisfied by current evidence.",
    },
    unknown: {
      tone: "neutral",
      label: decision.total
        ? "Gates require validation"
        : demo
          ? "Requires validation"
          : "Not measured",
      detail: decision.total
        ? `${decision.total} policy gates require validation against production thresholds before they can gate merges.`
        : "No policy evaluations were returned for this assessment.",
    },
  };
  const state = banner[decision.state];

  return (
    <div className="space-y-4">
      <div
        className={`asr-panel border-l-4 p-4 ${
          state.tone === "critical"
            ? "border-l-critical"
            : state.tone === "warning"
              ? "border-l-warning"
              : state.tone === "success"
                ? "border-l-success"
                : "border-l-border"
        }`}
        role="status"
      >
        <div className="flex flex-wrap items-center gap-3">
          <Pill tone={state.tone} icon={<GitPullRequest className="h-3.5 w-3.5" aria-hidden="true" />} className="px-3 py-1.5 text-sm">
            {state.label}
          </Pill>
          {decision.total > 0 && (
            <span className="text-xs text-muted">
              {decision.passing} passing · {decision.review} review · {decision.failing} failing
              &nbsp;of {decision.total} gates
            </span>
          )}
        </div>
        <p className="mt-2 max-w-2xl text-xs leading-5 text-text2">{state.detail}</p>
      </div>

      <SectionPanel
        title="Policy gates"
        description="Each gate is evaluated against measured evidence on every scan"
        flush
      >
        {policies.length ? (
          <div className="overflow-x-auto">
            <table className="asr-table">
              <thead>
                <tr>
                  <th scope="col">Gate</th>
                  <th scope="col">Policy</th>
                  <th scope="col">Status</th>
                  <th scope="col">Evidence</th>
                  <th scope="col">Owner</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((policy, index) => (
                  <tr key={policy.id || index}>
                    <td className="whitespace-nowrap font-mono text-xs text-muted">{policy.id}</td>
                    <td className="max-w-[260px] font-medium text-text">{policy.name}</td>
                    <td className="whitespace-nowrap">
                      <StatusPill status={policy.status} demo={demo} />
                    </td>
                    <td className="max-w-md text-muted">{policy.evidence || "—"}</td>
                    <td className="whitespace-nowrap">{policy.owner || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-4">
            <EmptyPanel demo={demo} />
          </div>
        )}
      </SectionPanel>
    </div>
  );
}
